import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Portal } from "@air/primitive-portal";
import { tailwindMerge } from "@air/tailwind-variants";
import { reportErrorToBugsnag } from "@air/utils-error";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useURLBoardIdSelector } from "~/hooks/useURLBoardIdSelector";
import { useWorkspaceHorizontalPadding } from "~/hooks/useWorkspaceHorizontalPadding";
import { activeTableColumnsSelector } from "~/store/configViews/selectors";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { formatActiveTableColumns, GENERIC_BOARD_ID, getColumnWidthMap, getColumnWidthVariable, getInitialColumnWidth, getMaxColumnWidth, getMinColumnWidth, getTableWidthVariable, getTableWidthVariableValue, getWidthsFromLocalStorage, makeUpdaterForColumnSizing, saveWidthsToLocalStorageThrottled, setTableDimensionsAsCSSVariables } from "./tableColumnWidthsUtils";
const defaultValue = {
    getHeaderResizeHandle: ()=>null,
    getColumnWidthStyle: ()=>({}),
    getTableWidthStyle: ()=>({}),
    getTableWidthAsNumber: ()=>0
};
export const TableViewContext = /*#__PURE__*/ createContext(defaultValue);
export const TableViewProvider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    var _useURLBoardIdSelector;
    // Board ID is `generic` when the table doesn't related to a board, e.g. "All assets & files"
    const boardId = (_useURLBoardIdSelector = useURLBoardIdSelector()) !== null && _useURLBoardIdSelector !== void 0 ? _useURLBoardIdSelector : GENERIC_BOARD_ID;
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const [columnSizing, setColumnSizing] = useState({});
    const [columnSizingInfo, setColumnSizingInfo] = useState({
        startOffset: 0,
        startSize: 0,
        deltaOffset: 0,
        deltaPercentage: 0,
        isResizingColumn: false,
        columnSizingStart: []
    });
    const activeTableColumns = useSelector(activeTableColumnsSelector);
    const tableColumns = useMemo(()=>formatActiveTableColumns(activeTableColumns), [
        activeTableColumns
    ]);
    // Tanstack Table FAQ strongly recommends stable data and columns
    // Reference: https://tanstack.com/table/latest/docs/faq
    const data = useMemo(()=>{
        return [];
    }, []);
    const columns = useMemo(()=>{
        return tableColumns.map((column)=>({
                accessorKey: column.fieldId,
                minSize: getMinColumnWidth(column.fieldId),
                maxSize: getMaxColumnWidth(column.fieldId)
            }));
    }, [
        tableColumns
    ]);
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        onColumnSizingChange: setColumnSizing,
        onColumnSizingInfoChange: (updater)=>{
            setColumnSizingInfo(updater);
            const newColumnSizingInfo = typeof updater === "function" ? updater(columnSizingInfo) : updater;
            const columnIsResizing = !!(newColumnSizingInfo.isResizingColumn && newColumnSizingInfo.startSize && newColumnSizingInfo.deltaOffset);
            const newColumnSizing = {
                ...Object.fromEntries(Object.entries(columnSizing).map((param)=>{
                    let [key, value] = param;
                    return [
                        key,
                        Math.min(Math.max(value, getMinColumnWidth(key)), getMaxColumnWidth(key))
                    ];
                })),
                ...!!(newColumnSizingInfo.isResizingColumn && newColumnSizingInfo.startSize && newColumnSizingInfo.deltaOffset) && {
                    [newColumnSizingInfo.isResizingColumn]: Math.min(Math.max(newColumnSizingInfo.startSize + newColumnSizingInfo.deltaOffset, getMinColumnWidth(newColumnSizingInfo.isResizingColumn)), getMaxColumnWidth(newColumnSizingInfo.isResizingColumn))
                }
            };
            if (columnIsResizing) {
                const columnWidthMap = getColumnWidthMap(tableColumns, newColumnSizing);
                table.setColumnSizing(makeUpdaterForColumnSizing(Object.fromEntries(columnWidthMap.entries())));
                setTableDimensionsAsCSSVariables(boardId, tableColumns, columnWidthMap, responsiveHorizontalPadding);
                saveWidthsToLocalStorageThrottled(boardId, Object.fromEntries(columnWidthMap));
            }
        },
        initialState: {
            columnSizing,
            columnSizingInfo
        },
        state: {
            columnSizing,
            columnSizingInfo
        }
    });
    // Purpose: Load column widths from localStorage when boardId/columns change
    useEffect(()=>{
        const savedColumnWidths = getWidthsFromLocalStorage(boardId);
        const currentWidths = {};
        for (const column of tableColumns){
            currentWidths[column.fieldId] = savedColumnWidths[column.fieldId] || getInitialColumnWidth(column.fieldId);
        }
        const columnWidthMap = getColumnWidthMap(tableColumns, currentWidths);
        table.setColumnSizing(makeUpdaterForColumnSizing(Object.fromEntries(columnWidthMap.entries())));
        setTableDimensionsAsCSSVariables(boardId, tableColumns, columnWidthMap, responsiveHorizontalPadding);
    }, [
        boardId,
        tableColumns,
        table,
        responsiveHorizontalPadding
    ]);
    // Purpose: Render a resize handle for the header of the column
    const getHeaderResizeHandle = useCallback((activeTableColumn)=>{
        const header = table.getFlatHeaders().find((header)=>header.id === activeTableColumn.fieldName);
        if (!header) {
            return null;
        }
        const isResizing = header.column.getIsResizing();
        return /*#__PURE__*/ _jsxs("div", {
            role: "button",
            "data-draggable": "true",
            onMouseDown: header.getResizeHandler(),
            onTouchStart: header.getResizeHandler(),
            className: tailwindMerge(// base
            "group absolute", "bottom-0 right-0 top-0 h-full w-3 cursor-col-resize touch-none select-none"),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: tailwindMerge(// base
                    "absolute bottom-3 left-[5px] top-3 z-0 w-px bg-grey-5", // hover
                    "group-hover:top-0 group-hover:h-full", isResizing && "top-0 h-full")
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: tailwindMerge(// base
                    "absolute left-[4px] top-0 z-1 h-full w-[3px] bg-blue-9 opacity-0", // hover
                    "transition-opacity duration-300 group-hover:opacity-100", isResizing && "opacity-100")
                }),
                isResizing && /*#__PURE__*/ _jsx(Portal, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "fixed inset-0 z-[9999] size-full cursor-col-resize bg-transparent"
                    })
                })
            ]
        });
    }, [
        table
    ]);
    // Purpose: Render a style for the table width
    const getTableWidthStyle = useCallback(()=>{
        const tableWidth = getTableWidthVariableValue(boardId);
        return {
            minWidth: tableWidth ? getTableWidthVariable(boardId) : "100%"
        };
    }, [
        boardId
    ]);
    const getTableWidthAsNumber = useCallback(()=>{
        const tableWidth = getTableWidthVariableValue(boardId);
        return tableWidth ? parseInt(tableWidth) : 0;
    }, [
        boardId
    ]);
    // Purpose: Render a style for the column width
    const getColumnWidthStyle = useCallback((activeTableColumn)=>{
        const columnVariable = getColumnWidthVariable(boardId, activeTableColumn.fieldName);
        return {
            flex: "0 1 ".concat(columnVariable),
            minWidth: columnVariable
        };
    }, [
        boardId
    ]);
    const providerValue = useMemo(()=>({
            getHeaderResizeHandle,
            getColumnWidthStyle,
            getTableWidthStyle,
            getTableWidthAsNumber
        }), [
        getHeaderResizeHandle,
        getColumnWidthStyle,
        getTableWidthStyle,
        getTableWidthAsNumber
    ]);
    return /*#__PURE__*/ _jsx(TableViewContext.Provider, {
        value: providerValue,
        children: children
    });
});
TableViewProvider.displayName = "TableViewProvider";
export const useTableView = ()=>{
    const context = useContext(TableViewContext);
    if (!context) {
        const error = new Error("useTableView must be used within a TableViewProvider");
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error.message
            });
        }
    }
    return context;
};
